import type { FormEvent } from 'react';
import React, { Suspense, useState } from 'react';
import { CreativeTypes, type MultiPageVideoModel, type ShapeElement } from '@bynder-studio/render-core';
import { Divider, Form } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useAccessRights from 'packages/hooks/useAccessRights';
import useForceUpdate from '~/hooks/useForceUpdate';
import useEditorSelectedElement from '~/hooks/useEditorSelectedElement';
import useEditor from '~/hooks/useEditor';
import features from '~/configs/features';
import { SizeFormRow } from '../index.styled';
import { FormSectionContent, InspectorContent, InspectorHeader, Section } from '../../index.styled';
import SettingsTabs from '../Tabs';
import Opacity from '../Opacity';
import ElementAlignment from '../ElementAlignment';
import Width from '../Size/Width';
import LockToggler from '../Size/LockToggle';
import Height from '../Size/Height';
import HorizontalPosition from '../Position/Horizontal';
import VerticalPosition from '../Position/Vertical';
import Rotation from '../Rotation';
import { ShapeSelect } from './ShapeSelect';
import CornerRadius from './BorderRadius';
import Masking from '../Masking';
import Duration from '../Duration';
import Shadow from '../Shadow';
import { FillColor } from './FillColor';
import BorderColor from './BorderColor';
import ShapeBorderWeight from './ShapeBorderWeight';
import Animations from '../../Animations';
import { BlendMode } from '../BlendMode';
import AllowToggleVisibility from '../AllowToggleVisibility';
import ContentProperty from '../ContentProperty';
import { useEffectivelyLocked } from 'packages/hooks/useEffectivelyLocked';
import useEditorSidebarShortcuts from 'packages/hooks/shortcuts/useEditorSidebarShortcuts';
import { parseShortcutForTooltip } from 'packages/common/WithShortcutLabel';
import MaybeTooltip from 'packages/common/MaybeTooltip';

const Shape = () => {
    const forceUpdate = useForceUpdate();
    const { isPlatformAllowed } = useAccessRights();
    const { selectedElement, selectedElements } = useEditorSelectedElement<ShapeElement>();
    const { translate } = useTranslate();
    const { creativeModel, creativeType, frameRate, manipulationRenderer } = useEditor();
    const isLockedByParent = useEffectivelyLocked();

    const tabs = [
        { value: 'shape', label: translate('editor.sidebar.shots.shape.tabs.text') },
        { value: 'shape_animation', label: translate('editor.sidebar.shots.animation.tabs.text') },
    ];

    const [activeTab, setTab] = useState(tabs[0].value);

    const defaultProps = {
        selectedElement,
        creativeModel,
        disabled: isLockedByParent,
    };

    useEditorSidebarShortcuts({
        setAnimationsTab: () => {
            if (creativeType === 'VIDEO') {
                setTab(tabs[1].value);
            }
        },
    });

    return (
        <>
            {creativeType === 'VIDEO' && (
                <InspectorHeader aria-labelledby="shape-text-tabs-settings">
                    <SettingsTabs tabs={tabs} activeTab={activeTab} onChange={(value) => setTab(value)} />
                </InspectorHeader>
            )}
            <InspectorContent withTabs={creativeType === 'VIDEO'}>
                {activeTab === tabs[0].value && (
                    <>
                        <Section aria-labelledby="shots-alignment-settings">
                            <ElementAlignment selectedElements={selectedElements} creativeModel={creativeModel} />
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shape-size-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <SizeFormRow>
                                    <Width {...defaultProps} creativeType={creativeType} />
                                    <LockToggler selectedElement={selectedElement} forceUpdate={forceUpdate} />
                                    <Height {...defaultProps} creativeType={creativeType} />
                                </SizeFormRow>
                                <Form.Row>
                                    <HorizontalPosition {...defaultProps} />
                                    <VerticalPosition {...defaultProps} />
                                </Form.Row>
                                <Form.Row>
                                    <Rotation {...defaultProps} />
                                    <Opacity {...defaultProps} />
                                </Form.Row>
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        {isPlatformAllowed([features.CONTENT_PROPERTIES]) && (
                            <>
                                <Section aria-labelledby="shots-text-content-property">
                                    <Suspense>
                                        <ContentProperty
                                            creativeModel={creativeModel}
                                            selectedElements={selectedElements}
                                            disabled={defaultProps.disabled}
                                        />
                                    </Suspense>
                                </Section>
                                <Divider />
                            </>
                        )}
                        <Section aria-labelledby="shape-dropdown">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <ShapeSelect {...defaultProps} />
                                <CornerRadius {...defaultProps} />
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shape-fill-color">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <FillColor {...defaultProps} />
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shape-border-color">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <BorderColor {...defaultProps} />
                                <ShapeBorderWeight {...defaultProps} />
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        {creativeType === CreativeTypes.VIDEO && (
                            <>
                                <Section aria-labelledby="shape-duration">
                                    <Form onSubmit={preventSubmit}>
                                        <Form.Group>
                                            <Duration
                                                {...defaultProps}
                                                creativeModel={creativeModel as MultiPageVideoModel}
                                                frameRate={frameRate}
                                            />
                                        </Form.Group>
                                    </Form>
                                </Section>
                                <Divider />
                            </>
                        )}
                        <Section aria-labelledby="shots-video-visibility">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.element.visibility_control')}</Form.Label>
                                    <MaybeTooltip
                                        content={`${translate(
                                            'editor.element.visibility_control.tooltip',
                                        )} (${parseShortcutForTooltip(['s'])})`}
                                        hasTooltip={!defaultProps.disabled}
                                    >
                                        <AllowToggleVisibility
                                            creativeModel={creativeModel}
                                            selectedElements={[selectedElement]}
                                            disabled={defaultProps.disabled}
                                        />
                                    </MaybeTooltip>
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shape-mask">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.element.mask')}</Form.Label>
                                    <Masking
                                        {...defaultProps}
                                        manipulationRenderer={manipulationRenderer}
                                        creativeType={creativeType}
                                    />
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shape-shadow">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.element.effects')}</Form.Label>
                                    <Shadow {...defaultProps} />
                                </Form.Group>
                                <Divider />
                                <Form.Group>
                                    <BlendMode disabled={defaultProps.disabled} />
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                    </>
                )}
                {activeTab === tabs[1].value && <Animations element={selectedElement} disabled={isLockedByParent} />}
            </InspectorContent>
        </>
    );
};

export default Shape;

function preventSubmit(e: FormEvent) {
    e.preventDefault();
}
